import { UseQueryResult } from "@tanstack/react-query";
import {
  SatelliteFill18Icon,
  SatelliteOutline18Icon,
} from "@vygruppen/spor-icon-react";
import EditInternalMessageModal from "features/CenterContent/RoleContent/InternalMessage/EditInternalMessageModal";
import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import { InternalMessageEvents } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/InternalMessageEvents";
import { partitionInternalMessagesRelatedToEvent } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/utils";
import { Tabs, TabsSides } from "features/CenterContent/shared/Tabs/TabsSides";
import { FC, useMemo, useState } from "react";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import {
  DialogRoot,
  DropsDialogContent,
  DropsDialogHeader,
  DialogBody as SporDialogBody,
} from "shared/components/Spor";
import { ObtrusiveScrollCss } from "shared/components/transitions/scroll";
import { NoteFill18Icon, NoteOutline18Icon } from "shared/icons/NoteIcon";
import styled from "styled-components";

const DialogBody = styled(SporDialogBody)`
  ${ObtrusiveScrollCss};
  overflow-y: auto;
  display: grid;

  /* Hide scrollbar */
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

const tabs: Tabs[] = [
  {
    id: "internalMessagesRelatedToEvent",
    iconInactive: <SatelliteOutline18Icon />,
    iconActive: <SatelliteFill18Icon />,
    text: `Tilknyttet hendelse`,
  },
  {
    id: "internalMessagesNotRelatedToEvent",
    iconInactive: <NoteOutline18Icon />,
    iconActive: <NoteFill18Icon />,
    text: `Frittstående meldinger`,
  },
];

type UpcomingEventsModalProps = {
  onClose: () => void;
  internalMessageEvents?: UseQueryResult<InternalMessageResponse[]>;
};

export const InternalEventsOverviewModal: FC<UpcomingEventsModalProps> = ({
  onClose,
  internalMessageEvents,
}) => {
  const [internalMessagesNotRelatedToEvent, internalMessagesRelatedToEvent] =
    partitionInternalMessagesRelatedToEvent(internalMessageEvents?.data ?? []);

  const notifications = useMemo(
    () => ({
      internalMessagesRelatedToEvent: internalMessagesRelatedToEvent.length,
      internalMessagesNotRelatedToEvent:
        internalMessagesNotRelatedToEvent.length,
    }),
    [internalMessagesNotRelatedToEvent, internalMessagesRelatedToEvent],
  );

  const [selectedTabIds, setSelectedTabIds] = useState([
    "internalMessagesRelatedToEvent",
  ]);

  const [internalEventToEdit, setInternalEventToEdit] =
    useState<InternalMessageResponse | null>(null);

  const isPending = internalMessageEvents?.isPending;

  const isFailure = internalMessageEvents?.isError;

  if (internalEventToEdit) {
    return (
      <EditInternalMessageModal
        setModalOpen={onClose}
        uuid={internalEventToEdit.uuid}
      />
    );
  }
  return (
    <DialogRoot open onOpenChange={onClose} size="lg" placement="center">
      <DropsDialogContent maxH="80vh" minH="80vh" minW="700px">
        <DropsDialogHeader>Interne meldinger</DropsDialogHeader>
        <TabsSides
          tabs={tabs}
          selectedTabIds={selectedTabIds}
          setSelectedTabIds={setSelectedTabIds}
          justifyContent="start"
          notifications={notifications}
          alwaysShowNotifications
          ml="18px"
          inModal
        />
        <DialogBody p="24px">
          {isPending && <SkeletonLoader skeletonType="internalEvents" />}
          {isFailure && <FailureMessage />}

          {internalMessageEvents?.isSuccess &&
            selectedTabIds.includes("internalMessagesNotRelatedToEvent") && (
              <InternalMessageEvents
                data={internalMessagesNotRelatedToEvent}
                onEditClick={(event) => {
                  setInternalEventToEdit(event);
                }}
              />
            )}

          {internalMessageEvents?.isSuccess &&
            selectedTabIds.includes("internalMessagesRelatedToEvent") && (
              <InternalMessageEvents
                data={internalMessagesRelatedToEvent}
                onEditClick={(event) => {
                  setInternalEventToEdit(event);
                }}
              />
            )}
        </DialogBody>
      </DropsDialogContent>
    </DialogRoot>
  );
};
